import { useEffect, useRef } from "react";
import { Animated, Modal as RNModal, Platform, Text, View } from "react-native";

import { COLORS, FONTS } from "../../lib/brand";
import { PANE_THEMES } from "../brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;
import Svg, { Circle, Rect } from "react-native-svg";


/**
 * Branded exit animation. Renders a full-bleed sand cover with the
 * Coravu wordmark + mark centred, fading IN over the current page.
 * After ~900ms the onComplete callback fires — typically used to
 * trigger the actual sign-out + navigation, so the user sees a clean
 * brand-aligned exit instead of an instant page swap.
 */
export function BrandExit({ onComplete }: { onComplete: () => void }) {
  const opacity = useRef(new Animated.Value(0)).current;
  const markScale = useRef(new Animated.Value(1.04)).current;
  const markOpacity = useRef(new Animated.Value(0)).current;
  const fired = useRef(false);

  useEffect(() => {
    Animated.sequence([
      Animated.timing(opacity, {
        toValue: 1,
        duration: 320,
        useNativeDriver: true,
      }),
      Animated.parallel([
        Animated.timing(markOpacity, {
          toValue: 1,
          duration: 260,
          useNativeDriver: true,
        }),
        Animated.timing(markScale, {
          toValue: 1,
          duration: 480,
          useNativeDriver: true,
        }),
      ]),
      Animated.delay(280),
    ]).start(() => {
      if (!fired.current) {
        fired.current = true;
        onComplete();
      }
    });
  }, [onComplete, opacity, markOpacity, markScale]);

  return (
    <RNModal transparent visible animationType="none" hardwareAccelerated>
    <Animated.View
      pointerEvents="none"
      style={[
        Platform.OS === "web"
          ? ({
              position: "fixed" as unknown as "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: COLORS.SAND.hex,
              alignItems: "center",
              justifyContent: "center",
              zIndex: 99999,
            } as object)
          : {
              position: "absolute" as const,
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: COLORS.SAND.hex,
              alignItems: "center",
              justifyContent: "center",
              zIndex: 99999,
            },
        { opacity },
      ]}
    >
      {Platform.OS === "web" ? (
        <View
          pointerEvents="none"
          style={[
            {
              position: "absolute",
              top: "30%" as unknown as number,
              left: "50%" as unknown as number,
              width: 480,
              height: 480,
              marginLeft: -240,
              marginTop: -180,
              borderRadius: 240,
            },
            {
              backgroundImage:
                `radial-gradient(circle, ${STUDENT_THEME.accentTintSoft} 0%, transparent 60%)`,
            } as object,
          ]}
        />
      ) : null}

      <Animated.View
        style={{
          alignItems: "center",
          opacity: markOpacity,
          transform: [{ scale: markScale }],
        }}
      >
        <View
          style={{
            width: 64,
            height: 64,
            borderRadius: 14,
            backgroundColor: COLORS.FOREST.hex,
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 18,
          }}
        >
          <Svg width={40} height={40} viewBox="0 0 34 34" fill="none">
            <Rect width={34} height={34} rx={7} fill={COLORS.FOREST_MID.hex} />
            <Circle cx={17} cy={17} r={11} stroke={COLORS.SAGE.hex} strokeWidth={0.7} />
            <Circle cx={17} cy={17} r={6.5} stroke={COLORS.AMBER.hex} strokeWidth={1.4} />
            <Circle cx={17} cy={17} r={2} fill={COLORS.AMBER.hex} />
          </Svg>
        </View>
        <Text
          style={{
            fontFamily: FONTS.displayMedium,
            fontSize: 36,
            letterSpacing: 5,
            color: COLORS.FOREST.hex,
          }}
        >
          COR<Text style={{ color: COLORS.AMBER.hex }}>A</Text>VU
        </Text>
        <Text
          style={{
            marginTop: 8,
            fontFamily: FONTS.bodyMedium,
            fontSize: 9,
            letterSpacing: 3,
            color: STUDENT_THEME.textOnSurfaceFaint,
            textTransform: "uppercase",
          }}
        >
          See you soon
        </Text>
      </Animated.View>
    </Animated.View>
    </RNModal>
  );
}
